/**
 * Tema do painel: Claro, Escuro ou Sistema (padrão de navegação A, item 6; 11/10/2026).
 *
 * Antes eram dois estados (claro/escuro) e o "sistema" só valia na primeira visita: depois do 1º clique a
 * pessoa nunca mais voltava a seguir o aparelho. Agora são três preferências; "Sistema" segue o
 * `prefers-color-scheme` ao vivo. NÃO há regra de horário (decisão do padrão: tema por hora do dia
 * surpreende quem está no plantão).
 *
 * Compatível com o que já estava gravado: `light`/`dark` continuam valendo; "Sistema" = chave AUSENTE
 * (é o que o script de `index.html` também entende, para não piscar branco no carregamento).
 */
import { useCallback, useEffect, useState } from "react";
import type { PreferenciaTema } from "../components/shell/tipos.js";

const CHAVE = "sentinela:tema";

export function lerPreferencia(): PreferenciaTema {
  try {
    const v = localStorage.getItem(CHAVE);
    if (v === "dark") return "escuro";
    if (v === "light") return "claro";
  } catch {
    /* armazenamento bloqueado: segue o sistema */
  }
  return "sistema";
}

export function sistemaEscuro(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** O tema que a tela mostra de fato, dada a preferência. */
export function temaEfetivo(p: PreferenciaTema): "claro" | "escuro" {
  if (p === "sistema") return sistemaEscuro() ? "escuro" : "claro";
  return p;
}

export function aplicarPreferencia(p: PreferenciaTema): void {
  const escuro = temaEfetivo(p) === "escuro";
  document.documentElement.classList.toggle("dark", escuro);
  try {
    if (p === "sistema") localStorage.removeItem(CHAVE);
    else localStorage.setItem(CHAVE, p === "escuro" ? "dark" : "light");
  } catch {
    /* modo privado: vale só nesta página */
  }
}

export function useTema() {
  const [preferencia, setPreferencia] = useState<PreferenciaTema>(() => lerPreferencia());

  useEffect(() => {
    aplicarPreferencia(preferencia);
    if (preferencia !== "sistema") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const aoMudar = () => aplicarPreferencia("sistema");
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, [preferencia]);

  const definir = useCallback((p: PreferenciaTema) => setPreferencia(p), []);
  return { preferencia, definir };
}
